import { useEffect, useState } from "react";
import type { StudioMarketingAsset, StudioMarketingFile } from "@itaas/pep-contracts";
import {
  campaignLabel,
  getAssetsForProduct,
  getPrimaryFile,
  getStudioMarketingIndex,
} from "../../api/studio-marketing";

interface StudioMarketingSectionProps {
  productSlug: string;
  compact?: boolean;
  hideHeader?: boolean;
}

function AssetPreview({ file, label }: { file: StudioMarketingFile; label: string }) {
  if (file.mimeType === "application/pdf") {
    return (
      <div className="ratio ratio-3x4 bg-body-tertiary">
        <object data={file.url} type="application/pdf" className="w-100 h-100" aria-label={label}>
          <a href={file.url} target="_blank" rel="noreferrer" className="d-flex h-100 align-items-center justify-content-center">
            Ouvrir le PDF
          </a>
        </object>
      </div>
    );
  }
  if (file.mimeType.startsWith("video/")) {
    return (
      <video
        src={file.url}
        controls
        className="w-100"
        style={{ maxHeight: "16rem", objectFit: "cover" }}
        preload="metadata"
      />
    );
  }
  return (
    <a href={file.url} target="_blank" rel="noreferrer" className="d-block">
      <img
        src={file.url}
        alt={label}
        className="w-100"
        style={{ objectFit: "cover", maxHeight: "16rem" }}
        loading="lazy"
      />
    </a>
  );
}

export function StudioMarketingSection({
  productSlug,
  compact = false,
  hideHeader = false,
}: StudioMarketingSectionProps) {
  const [assets, setAssets] = useState<StudioMarketingAsset[]>([]);

  useEffect(() => {
    getStudioMarketingIndex()
      .then((index) => setAssets(getAssetsForProduct(index, productSlug)))
      .catch(console.error);
  }, [productSlug]);

  if (assets.length === 0) return null;

  const brochures = assets.filter((a) => a.contentType === "brochure");
  const social = assets.filter((a) => a.contentType === "social-post");
  const videos = assets.filter((a) => a.contentType === "video");

  const groups = [
    { key: "print", title: "Print & direction", items: brochures },
    { key: "social", title: "Réseaux sociaux", items: social },
    { key: "video", title: "Vidéos", items: videos },
  ].filter((g) => g.items.length > 0);

  return (
    <section className={compact ? "mb-0" : "mb-5"} id={hideHeader ? undefined : "kit-campagne"}>
      {!hideHeader && (
      <div className="rounded-4 p-4 p-md-5 mb-4 text-white" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)" }}>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <p className="text-uppercase small fw-semibold mb-1 opacity-75">Kit campagne</p>
            <h2 className={compact ? "h5 fw-bold mb-1" : "h4 fw-bold mb-2"}>
              Assets prêts à partager
            </h2>
            <p className="mb-0 small opacity-90">
              Flyers print, posts LinkedIn, stories et vidéos — téléchargez et diffusez.
            </p>
          </div>
          <span className="btn btn-warning btn-sm fw-semibold disabled">
            {assets.length} visuel{assets.length > 1 ? "s" : ""}
          </span>
        </div>
      </div>
      )}

      {groups.map((group) => (
        <div key={group.key} className="mb-4">
          <h3 className="h6 text-uppercase text-secondary fw-bold mb-3">{group.title}</h3>
          <div className={compact ? "d-flex flex-column gap-3" : "row g-4"}>
            {group.items.map((asset) => {
              const primary = getPrimaryFile(asset);
              if (!primary) return null;
              const label = campaignLabel(asset.campaignId);

              return (
                <article key={asset.assetId} className={compact ? "" : "col-md-6 col-lg-4"}>
                  <div className="card border-0 shadow-sm h-100 overflow-hidden">
                    <AssetPreview file={primary} label={label} />
                    <div className="card-body p-3 d-flex flex-column">
                      <h4 className="h6 fw-bold mb-1">{label}</h4>
                      <p className="small text-secondary mb-3">
                        {asset.contentType === "brochure" && "PDF print A5 — salon, direction, WhatsApp"}
                        {asset.contentType === "social-post" && "PNG — LinkedIn, Facebook, Instagram"}
                        {asset.contentType === "video" && "MP4 — landing, stories, présentation"}
                      </p>
                      <div className="mt-auto d-flex flex-wrap gap-1">
                        <a
                          href={primary.url}
                          download
                          className="btn btn-primary btn-sm"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Télécharger
                        </a>
                        <a
                          href={primary.url}
                          className="btn btn-outline-secondary btn-sm"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Aperçu
                        </a>
                        {asset.files.length > 1 &&
                          asset.files.slice(1).map((file) => (
                            <a
                              key={file.variant}
                              href={file.url}
                              download
                              className="btn btn-outline-secondary btn-sm"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {file.variant}
                            </a>
                          ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
